/**
 * 알림장 컨디션 태그 — 명세서 §5.3 (총 12종 확정, §14.1-10 / §14.1-15)
 *
 * 태그는 데이터 주도(`ConditionTagDefinition`)다. 여기 정의는 "기본 세트" 시드이며,
 * 시설별 사용 여부·순서는 DB에서 편집한다. 구성 변경은 배포 없이 시드 데이터 수정으로 처리한다.
 */
import { AGE_BANDS } from './development.js';
export const CONDITION_TAG_CODES = [
    'MOOD',
    'HEALTH_SYMPTOM',
    'LUNCH',
    'SNACK_AM',
    'SNACK_PM',
    'FEEDING',
    'NAP',
    'URINE',
    'STOOL',
    'TEMPERATURE',
    'MEDICATION',
    'ACTIVITY',
];
export const CONDITION_VALUE_TYPES = {
    /** 아이콘 탭 단일 선택 */
    CHOICE: 'CHOICE',
    /** 아이콘 탭 다중 선택 */
    MULTI_CHOICE: 'MULTI_CHOICE',
    /** 횟수 스테퍼 */
    COUNT: 'COUNT',
    /** 시작–종료 시각 */
    TIME_RANGE: 'TIME_RANGE',
    /** 숫자 입력(체온 등) */
    NUMBER: 'NUMBER',
    /** 다른 모듈에서 자동 연동되는 읽기 전용 값 */
    LINKED: 'LINKED',
};
const ALL_BANDS = [
    AGE_BANDS.INFANT_EARLY,
    AGE_BANDS.INFANT,
    AGE_BANDS.TODDLER,
    AGE_BANDS.CHILD,
];
/** 0–1세(영아반) 전용 — 유아반 축소 세트에서는 제외 */
const INFANT_ONLY = [AGE_BANDS.INFANT_EARLY, AGE_BANDS.INFANT];
export const CONDITION_TAGS = [
    {
        code: 'MOOD',
        label: '기분',
        icon: 'smile',
        valueType: CONDITION_VALUE_TYPES.CHOICE,
        options: [
            { value: 'BRIGHT', label: '밝아요', icon: 'mood-bright' },
            { value: 'CALM', label: '차분해요', icon: 'mood-calm' },
            { value: 'EXCITED', label: '들떠 있어요', icon: 'mood-excited' },
            { value: 'SENSITIVE', label: '예민해요', icon: 'mood-sensitive' },
            { value: 'TEARFUL', label: '눈물이 많아요', icon: 'mood-tearful' },
        ],
        defaultForBands: ALL_BANDS,
        order: 1,
    },
    {
        code: 'HEALTH_SYMPTOM',
        label: '건강 상태',
        icon: 'stethoscope',
        valueType: CONDITION_VALUE_TYPES.MULTI_CHOICE,
        options: [
            { value: 'NONE', label: '특이사항 없음', icon: 'check' },
            { value: 'COUGH', label: '기침', icon: 'cough' },
            { value: 'RUNNY_NOSE', label: '콧물', icon: 'nose' },
            { value: 'SNEEZE', label: '재채기', icon: 'sneeze' },
            { value: 'RASH', label: '발진', icon: 'rash' },
            { value: 'VOMIT', label: '구토', icon: 'vomit' },
            { value: 'FATIGUE', label: '기운 없음', icon: 'fatigue' },
        ],
        defaultForBands: ALL_BANDS,
        order: 2,
    },
    {
        code: 'LUNCH',
        label: '점심 식사량',
        icon: 'bowl',
        valueType: CONDITION_VALUE_TYPES.CHOICE,
        options: [
            { value: 'ALL', label: '다 먹었어요', icon: 'meal-all' },
            { value: 'MOST', label: '많이 먹었어요', icon: 'meal-most' },
            { value: 'HALF', label: '반쯤 먹었어요', icon: 'meal-half' },
            { value: 'LITTLE', label: '조금 먹었어요', icon: 'meal-little' },
            { value: 'NONE', label: '먹지 않았어요', icon: 'meal-none' },
        ],
        defaultForBands: ALL_BANDS,
        order: 3,
    },
    {
        code: 'SNACK_AM',
        label: '오전 간식',
        icon: 'cookie',
        valueType: CONDITION_VALUE_TYPES.CHOICE,
        options: [
            { value: 'ALL', label: '다 먹었어요', icon: 'meal-all' },
            { value: 'HALF', label: '반쯤 먹었어요', icon: 'meal-half' },
            { value: 'LITTLE', label: '조금 먹었어요', icon: 'meal-little' },
            { value: 'NONE', label: '먹지 않았어요', icon: 'meal-none' },
        ],
        defaultForBands: ALL_BANDS,
        order: 4,
    },
    {
        code: 'SNACK_PM',
        label: '오후 간식',
        icon: 'cookie',
        valueType: CONDITION_VALUE_TYPES.CHOICE,
        options: [
            { value: 'ALL', label: '다 먹었어요', icon: 'meal-all' },
            { value: 'HALF', label: '반쯤 먹었어요', icon: 'meal-half' },
            { value: 'LITTLE', label: '조금 먹었어요', icon: 'meal-little' },
            { value: 'NONE', label: '먹지 않았어요', icon: 'meal-none' },
        ],
        defaultForBands: ALL_BANDS,
        order: 5,
    },
    {
        code: 'FEEDING',
        label: '수유·이유식',
        icon: 'bottle',
        valueType: CONDITION_VALUE_TYPES.COUNT,
        unit: '회',
        min: 0,
        max: 12,
        step: 1,
        defaultForBands: INFANT_ONLY,
        order: 6,
    },
    {
        code: 'NAP',
        label: '낮잠',
        icon: 'moon',
        valueType: CONDITION_VALUE_TYPES.TIME_RANGE,
        defaultForBands: ALL_BANDS,
        order: 7,
    },
    {
        code: 'URINE',
        label: '소변',
        icon: 'droplet',
        valueType: CONDITION_VALUE_TYPES.COUNT,
        unit: '회',
        min: 0,
        max: 15,
        step: 1,
        defaultForBands: INFANT_ONLY,
        order: 8,
    },
    {
        code: 'STOOL',
        label: '대변',
        icon: 'toilet',
        valueType: CONDITION_VALUE_TYPES.MULTI_CHOICE,
        options: [
            { value: 'NORMAL', label: '보통', icon: 'stool-normal' },
            { value: 'SOFT', label: '무름', icon: 'stool-soft' },
            { value: 'HARD', label: '단단함', icon: 'stool-hard' },
            { value: 'NONE', label: '없음', icon: 'stool-none' },
        ],
        defaultForBands: INFANT_ONLY,
        order: 9,
    },
    {
        code: 'TEMPERATURE',
        label: '체온',
        icon: 'thermometer',
        valueType: CONDITION_VALUE_TYPES.NUMBER,
        unit: '℃',
        min: 34,
        max: 42,
        step: 0.1,
        linkedModule: 'HEALTH',
        defaultForBands: ALL_BANDS,
        order: 10,
        hint: '건강기록에 재면 자동으로 채워져요',
    },
    {
        code: 'MEDICATION',
        label: '투약',
        icon: 'pill',
        valueType: CONDITION_VALUE_TYPES.LINKED,
        linkedModule: 'MEDICATION',
        defaultForBands: ALL_BANDS,
        order: 11,
        hint: '투약의뢰서를 완료 처리하면 자동으로 채워져요',
    },
    {
        code: 'ACTIVITY',
        label: '활동 참여',
        icon: 'palette',
        valueType: CONDITION_VALUE_TYPES.CHOICE,
        options: [
            { value: 'ENGAGED', label: '푹 빠져 참여했어요', icon: 'activity-engaged' },
            { value: 'JOINED', label: '함께 참여했어요', icon: 'activity-joined' },
            { value: 'WATCHED', label: '지켜보며 함께했어요', icon: 'activity-watched' },
            { value: 'RESTED', label: '쉬어 갔어요', icon: 'activity-rested' },
        ],
        defaultForBands: ALL_BANDS,
        order: 12,
    },
];
export function conditionTagsForBand(band) {
    return CONDITION_TAGS.filter((t) => t.defaultForBands.includes(band)).sort((a, b) => a.order - b.order);
}
export function getConditionTag(code) {
    return CONDITION_TAGS.find((t) => t.code === code);
}
/** 태그 값의 사람이 읽는 표현 */
export function formatConditionValue(code, value) {
    const tag = getConditionTag(code);
    if (!tag || value == null)
        return '';
    switch (tag.valueType) {
        case CONDITION_VALUE_TYPES.CHOICE:
            return tag.options?.find((o) => o.value === value)?.label ?? String(value);
        case CONDITION_VALUE_TYPES.MULTI_CHOICE: {
            const list = Array.isArray(value) ? value : [value];
            return list
                .map((v) => tag.options?.find((o) => o.value === v)?.label ?? String(v))
                .join(', ');
        }
        case CONDITION_VALUE_TYPES.COUNT:
            return `${value}${tag.unit ?? ''}`;
        case CONDITION_VALUE_TYPES.NUMBER:
            return `${value}${tag.unit ?? ''}`;
        case CONDITION_VALUE_TYPES.TIME_RANGE: {
            const v = value;
            if (!v?.start)
                return '';
            return v.end ? `${v.start} – ${v.end}` : `${v.start} ~`;
        }
        case CONDITION_VALUE_TYPES.LINKED:
            return typeof value === 'string' ? value : JSON.stringify(value);
        default:
            return String(value);
    }
}
//# sourceMappingURL=condition-tags.js.map